import type { ReactNode } from 'react';

import type {
  TAccordionContainerElement,
  TAccordionContentElement,
  TAccordionElement,
  TAccordionTitleElement,
  TAudioPlayerElement,
  TAuthorsElement,
  TBulletedListElement,
  TButtonElement,
  TColumnElement,
  TColumnsElement,
  TContactFormElement,
  TContainerElement,
  TContentGateElement,
  TContentGatePopupElement,
  TDividerElement,
  TEmailCaptureElement,
  TEmailCapturePopupElement,
  TEmbedElement,
  TextLeafProps,
  THeadingElement,
  TIconElement,
  TImageElement,
  TListItemElement,
  TLoginElement,
  TLoginModalElement,
  TNumberedListElement,
  TParagraphElement,
  TPodcastPlatformsElement,
  TPodcastTranscriptElement,
  TPostActionsElement,
  TPostCommentsElement,
  TPostContentElement,
  TPostElement,
  TPostHeaderElement,
  TPricingElement,
  TProductReviewsElement,
  TProductsElement,
  TProgressIndicatorElement,
  TRecommendationsElement,
  TRecommendationsModalElement,
  TRecommendedPostsElement,
  TResetPasswordElement,
  TRichTextElement,
  TRootElement,
  TRSSFeedElement,
  TRSSFeedSearchElement,
  TSectionElement,
  TSignupElement,
  TSignupModalElement,
  TSocialsElement,
  TSubscriberGateElement,
  TSurveyElement,
  TTagsElement,
  TTestimonialsElement,
  TUpdatePasswordElement,
} from '@shared/dream-components';

import type { NodeContent } from '~/interfaces/page';

import { Accordion, AccordionContainer, AccordionContent, AccordionTitle } from './nodes/Accordion';
import AudioPlayer from './nodes/AudioPlayer';
import Authors from './nodes/Authors';
import Button from './nodes/Button/';
import Column from './nodes/Column';
import Columns from './nodes/Columns';
import ContactForm from './nodes/ContactForm';
import Container from './nodes/Container';
import { ContentGate } from './nodes/ContentGate';
import { ContentGatePopup } from './nodes/ContentGatePopup';
import Divider from './nodes/Divider';
import Doc from './nodes/Doc';
import EmailCapture from './nodes/EmailCapture';
import EmailCapturePopup from './nodes/EmailCapturePopup';
import Embed from './nodes/Embed';
import Heading from './nodes/Heading';
import Icon from './nodes/Icon';
import Image from './nodes/Image';
import { BulletedList, ListItem, OrderedList } from './nodes/Lists';
import Login from './nodes/Login';
import LoginModal from './nodes/LoginModal';
import Paragraph from './nodes/Paragraph';
import PodcastPlatforms from './nodes/PodcastPlatforms';
import PodcastTranscript from './nodes/PodcastTranscript';
import { PostActions } from './nodes/PostActions';
import PostComments from './nodes/PostComments';
import PostContainer from './nodes/PostContainer';
import { PostContent } from './nodes/PostContent';
import PostHeader from './nodes/PostHeader';
import Pricing from './nodes/Pricing';
import ProductReviews from './nodes/ProductReviews';
import Products from './nodes/Products';
import ProgressIndicator from './nodes/ProgressIndicator';
import type { ProprietaryFooterNode } from './nodes/ProprietaryFooter';
import { ProprietaryFooter } from './nodes/ProprietaryFooter';
import Recommendations from './nodes/Recommendations';
import RecommendationsModal from './nodes/RecommendationsModal';
import RecommendedPosts from './nodes/RecommendedPosts';
import ResetPassword from './nodes/ResetPassword';
import RichText from './nodes/RichText';
import RSSFeed from './nodes/RSSFeed';
import RSSFeedSearch from './nodes/RSSFeedSearch';
import Section from './nodes/Section';
import Signup from './nodes/Signup';
import SignupModal from './nodes/SignupModal';
import Socials from './nodes/Socials';
import SubscriberGate from './nodes/SubscriberGate';
import Survey from './nodes/Survey';
import Tags from './nodes/Tags';
import Testimonials from './nodes/Testimonials';
import Text from './nodes/Text';
import UpdatePassword from './nodes/UpdatePassword';

interface Props {
  node: NodeContent;
  children?: ReactNode | ReactNode[];
  rootClassName?: string;
}

const NodeRenderer = ({ node, children, rootClassName }: Props) => {
  switch (node.type) {
    case 'accordion':
      return <Accordion node={node as TAccordionElement}>{children}</Accordion>;
    case 'accordionTitle':
      return <AccordionTitle node={node as TAccordionTitleElement}>{children}</AccordionTitle>;
    case 'accordionContent':
      return <AccordionContent node={node as TAccordionContentElement}>{children}</AccordionContent>;
    case 'accordionContainer':
      return <AccordionContainer node={node as TAccordionContainerElement}>{children}</AccordionContainer>;
    case 'audioPlayer':
      return <AudioPlayer node={node as TAudioPlayerElement} />;
    case 'section':
      return <Section node={node as TSectionElement}>{children}</Section>;
    case 'doc':
      return (
        <Doc node={node as unknown as TRootElement} className={rootClassName}>
          {children}
        </Doc>
      );
    case 'paragraph':
      return <Paragraph node={node as TParagraphElement}>{children}</Paragraph>;
    case 'text':
      return <Text node={node as TextLeafProps['leaf']}>{children}</Text>;
    case 'heading':
      return <Heading node={node as unknown as THeadingElement}>{children}</Heading>;
    case 'button':
      return <Button node={node as TButtonElement}>{children}</Button>;
    case 'container':
      return <Container node={node as TContainerElement}>{children}</Container>;
    case 'signup':
      return <Signup node={node as TSignupElement}>{children}</Signup>;
    case 'testimonials':
      return <Testimonials node={node as TTestimonialsElement}>{children}</Testimonials>;
    case 'productReviews':
      return <ProductReviews node={node as TProductReviewsElement}>{children}</ProductReviews>;
    case 'columns':
      return <Columns node={node as TColumnsElement}>{children}</Columns>;
    case 'column':
      return <Column node={node as TColumnElement}>{children}</Column>;
    case 'image':
      return <Image node={node as TImageElement}>{children}</Image>;
    case 'post':
      return <PostContainer node={node as TPostElement}>{children}</PostContainer>;
    case 'recommendedPosts':
      return <RecommendedPosts node={node as unknown as TRecommendedPostsElement}>{children}</RecommendedPosts>;
    case 'postComments':
      return <PostComments node={node as unknown as TPostCommentsElement}>{children}</PostComments>;
    case 'socials':
      return <Socials node={node as TSocialsElement}>{children}</Socials>;
    case 'divider':
      return <Divider node={node as TDividerElement}>{children}</Divider>;
    case 'embed':
      return <Embed node={node as TEmbedElement}>{children}</Embed>;
    case 'authors':
      return <Authors node={node as TAuthorsElement}>{children}</Authors>;
    case 'pricing':
      return <Pricing node={node as TPricingElement}>{children}</Pricing>;
    case 'contactForm':
      return <ContactForm node={node as TContactFormElement}>{children}</ContactForm>;
    case 'survey':
      return <Survey node={node as TSurveyElement}>{children}</Survey>;
    case 'signupModal':
      return <SignupModal node={node as unknown as TSignupModalElement}>{children}</SignupModal>;
    case 'recommendations':
      return <Recommendations node={node as unknown as TRecommendationsElement}>{children}</Recommendations>;
    case 'icon':
      return <Icon node={node as TIconElement}>{children}</Icon>;
    case 'recommendationsModal':
      return (
        <RecommendationsModal node={node as unknown as TRecommendationsModalElement}>{children}</RecommendationsModal>
      );
    case 'login':
      return <Login node={node as TLoginElement}>{children}</Login>;
    case 'resetPassword':
      return <ResetPassword node={node as TResetPasswordElement}>{children}</ResetPassword>;
    case 'updatePassword':
      return <UpdatePassword node={node as TUpdatePasswordElement}>{children}</UpdatePassword>;
    case 'loginModal':
      return <LoginModal node={node as unknown as TLoginModalElement}>{children}</LoginModal>;
    case 'tags':
      return <Tags node={node as TTagsElement}>{children}</Tags>;
    case 'hardBreak':
      return <br />;
    case 'proprietaryFooter':
      return <ProprietaryFooter node={node as unknown as ProprietaryFooterNode} />;
    case 'progressIndicator':
      return <ProgressIndicator node={node as TProgressIndicatorElement}>{children}</ProgressIndicator>;
    case 'postContent':
      return <PostContent node={node as TPostContentElement}>{children}</PostContent>;
    case 'postHeader':
      return <PostHeader node={node as TPostHeaderElement}>{children}</PostHeader>;
    case 'postActions':
      return <PostActions node={node as TPostActionsElement}>{children}</PostActions>;
    case 'emailCapturePopup':
      return <EmailCapturePopup node={node as unknown as TEmailCapturePopupElement}>{children}</EmailCapturePopup>;
    case 'contentGate':
      return <ContentGate node={node as unknown as TContentGateElement}>{children}</ContentGate>;
    case 'contentGatePopup':
      return <ContentGatePopup node={node as unknown as TContentGatePopupElement}>{children}</ContentGatePopup>;
    case 'emailCapture':
      return <EmailCapture node={node as unknown as TEmailCaptureElement}>{children}</EmailCapture>;
    case 'subscriberGate':
      return <SubscriberGate node={node as unknown as TSubscriberGateElement}>{children}</SubscriberGate>;
    case 'listItem':
      return <ListItem node={node as unknown as TListItemElement}>{children}</ListItem>;
    case 'bulletList':
      return <BulletedList node={node as unknown as TBulletedListElement}>{children}</BulletedList>;
    case 'orderedList':
      return <OrderedList node={node as unknown as TNumberedListElement}>{children}</OrderedList>;
    case 'rss_feed':
      return <RSSFeed node={node as unknown as TRSSFeedElement}>{children}</RSSFeed>;
    case 'rss_feed_search':
      return <RSSFeedSearch node={node as unknown as TRSSFeedSearchElement}>{children}</RSSFeedSearch>;
    case 'products':
      return <Products node={node as unknown as TProductsElement}>{children}</Products>;
    case 'richText':
      return <RichText node={node as unknown as TRichTextElement}>{children}</RichText>;
    case 'podcastPlatforms':
      return <PodcastPlatforms node={node as unknown as TPodcastPlatformsElement}>{children}</PodcastPlatforms>;
    case 'podcastTranscript':
      return <PodcastTranscript node={node as unknown as TPodcastTranscriptElement}>{children}</PodcastTranscript>;
    default:
      return null;
  }
};

export default NodeRenderer;
